import { FC, useEffect, useState } from 'react'
import {
  Autocomplete, Box, Button, Chip, FormControl, InputAdornment,
  InputLabel, MenuItem, Select, TextField, Typography,
} from '@mui/material'
import { MdClose, MdRefresh, MdSearch } from 'react-icons/md'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { ListHCPArtesUseCase } from '../../../Usecase/HCP/Artworks/list-hcp-artworks-usecase'
import { ListHCPArtesColunaUseCase } from '../../../Usecase/HCP/Artworks/list-hcp-artworks-column-usecase'
import { FiltroPrazoType, useArteAndamento } from './art-progress.hook'
import { KanbanBoardHCPArtes } from '../../../Components/KanbanBoardHCPArtworks/kanban-board-hcp-artworks.component'
import { ArteDetalhesDrawer } from '../../../Components/HCP/ArtDetailsDrawer/art-details-drawer.component'

interface IArteAndamentoProps {
  listHCPArtesUseCase: ListHCPArtesUseCase
  listHCPArtesColunaUseCase: ListHCPArtesColunaUseCase
}

const OPCOES_PRAZO: { label: string; value: FiltroPrazoType }[] = [
  { label: 'Todos', value: 'todos' },
  { label: 'Atrasados', value: 'atrasado' },
  { label: 'No prazo', value: 'no_prazo' },
  { label: 'Sem prazo', value: 'sem_prazo' },
]

const ArteAndamento: FC<IArteAndamentoProps> = ({ listHCPArtesUseCase, listHCPArtesColunaUseCase }) => {
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null)

  const {
    fetchArtes, kanbanSchema, loading, loadingMore, lastUpdated,
    filtroDesigner, setFiltroDesigner,
    filtroPrazo, setFiltroPrazo,
    filtroEtiqueta, setFiltroEtiqueta,
    filtroProduto, setFiltroProduto,
    filtroBusca, setFiltroBusca,
    filtroAprovadoOS, setFiltroAprovadoOS,
    filtroItensPendentes, setFiltroItensPendentes,
    aprovadoHasMore, aprovadoTotal, aprovadoCarregados,
    semOSCarregados, comOSCarregados, semOSTotal, comOSTotal,
    loadMoreAprovados,
    limparFiltros, temFiltroAtivo,
    designersDisponiveis, etiquetasDisponiveis, produtosDisponiveis,
  } = useArteAndamento({ listHCPArtesUseCase, listHCPArtesColunaUseCase })

  const selectedTask = selectedTaskId ? (kanbanSchema?.tasks[selectedTaskId] ?? null) : null

  useEffect(() => { fetchArtes() }, [])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>

      {/* Cabeçalho sticky — sem truque de margem negativa */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          bgcolor: '#f5f7fb',
          pt: 3,
          pb: 2,
          mb: 1,
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        }}
      >
        {/* Título + botão atualizar */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Typography variant="h5" fontWeight={700}>
              Arte Final
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Fluxo 1 — Acompanhamento de andamento das artes
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<MdRefresh size={16} />}
              onClick={fetchArtes}
              disabled={loading}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Atualizar
            </Button>
            {lastUpdated && (
              <Typography variant="caption" color="text.disabled">
                Atualizado às {format(lastUpdated, 'HH:mm', { locale: ptBR })} · próxima em 5 min
              </Typography>
            )}
          </Box>
        </Box>

        {/* Barra de filtros */}
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Busca livre */}
          <TextField
            size="small"
            placeholder="Buscar pedido, cliente ou produto..."
            value={filtroBusca}
            onChange={(e) => setFiltroBusca(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MdSearch size={18} color="#9e9e9e" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 260, bgcolor: '#fff', borderRadius: 1 }}
          />

          {/* Filtro prazo */}
          <FormControl size="small" sx={{ minWidth: 150, bgcolor: '#fff' }}>
            <InputLabel>Prazo</InputLabel>
            <Select
              value={filtroPrazo}
              label="Prazo"
              onChange={(e) => setFiltroPrazo(e.target.value as FiltroPrazoType)}
            >
              {OPCOES_PRAZO.map((op) => (
                <MenuItem key={op.value} value={op.value}>{op.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Filtro produto (Autocomplete para permitir digitação) */}
          {produtosDisponiveis.length > 0 && (
            <Autocomplete
              size="small"
              options={produtosDisponiveis}
              getOptionLabel={(option) => option.label}
              value={produtosDisponiveis.find((p) => p.value === filtroProduto) || null}
              onChange={(_, newValue) => setFiltroProduto(newValue?.value || '')}
              renderInput={(params) => (
                <TextField {...params} label="Produto" sx={{ bgcolor: '#fff', borderRadius: 1 }} />
              )}
              sx={{ minWidth: 220 }}
            />
          )}

          {/* Filtro designer */}
          {designersDisponiveis.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 160, bgcolor: '#fff' }}>
              <InputLabel>Designer</InputLabel>
              <Select
                value={filtroDesigner}
                label="Designer"
                onChange={(e) => setFiltroDesigner(e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                {designersDisponiveis.map((d) => (
                  <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Filtro etiqueta */}
          {etiquetasDisponiveis.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 160, bgcolor: '#fff' }}>
              <InputLabel>Etiqueta</InputLabel>
              <Select
                value={filtroEtiqueta}
                label="Etiqueta"
                onChange={(e) => setFiltroEtiqueta(e.target.value)}
              >
                <MenuItem value="">Todas</MenuItem>
                {etiquetasDisponiveis.map((e) => (
                  <MenuItem key={e.value} value={e.value}>{e.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Limpar filtros */}
          {temFiltroAtivo && (
            <Chip
              label="Limpar filtros"
              size="small"
              onDelete={limparFiltros}
              deleteIcon={<MdClose size={14} />}
              onClick={limparFiltros}
              sx={{
                fontWeight: 600,
                bgcolor: '#ffebee',
                color: '#c62828',
                '& .MuiChip-deleteIcon': { color: '#c62828' },
              }}
            />
          )}
        </Box>
      </Box>

      {/* Kanban */}
      <KanbanBoardHCPArtes
        data={kanbanSchema}
        loading={loading}
        onCardClick={setSelectedTaskId}
        filtroAprovadoOS={filtroAprovadoOS}
        onFiltroAprovadoOSChange={setFiltroAprovadoOS}
        filtroItensPendentes={filtroItensPendentes}
        onFiltroItensPendentesChange={setFiltroItensPendentes}
        aprovadoHasMore={aprovadoHasMore}
        aprovadoTotal={aprovadoTotal}
        aprovadoCarregados={aprovadoCarregados}
        semOSCarregados={semOSCarregados}
        comOSCarregados={comOSCarregados}
        semOSTotal={semOSTotal}
        comOSTotal={comOSTotal}
        loadingMore={loadingMore}
        onLoadMore={loadMoreAprovados}
      />

      {/* Drawer de detalhes */}
      <ArteDetalhesDrawer
        task={selectedTask}
        onClose={() => setSelectedTaskId(null)}
      />
    </Box>
  )
}

export default ArteAndamento
